/**
 * VAT categories — admin reference-data hook (P22 follow-up #4).
 *
 * Replaces the hardcoded `VAT_CATEGORIES` constants in
 * `ProductDetailsFields.tsx` + `ExtractionDetail.tsx`. Fetches from
 * `GET /v1/admin/catalog/vat-categories` on mount; returns the live list,
 * or the FALLBACK list if the fetch fails (so admin select-dropdowns stay
 * usable on partial outage). A failure also logs once via console.warn —
 * the admin's runtime forwards console output to its log sink, no Sentry
 * SDK coupling here.
 *
 * Admin does NOT use TanStack Query (verified across the codebase). Plain
 * `useEffect` + `useState` is the established pattern (see `ExtractionDetail.tsx`).
 * The endpoint is sub-1ms against a 4-row table; refetch-on-mount has
 * negligible cost.
 */
import { useEffect, useState } from "react";
import type { VatCategoryDTO } from "@wmart/shared-types";
import { api } from "./api-client.js";

/**
 * Fallback list used when the API call fails. Mirrors the DB rows in
 * migration 0003. Drift here means console.warn fired (the endpoint is
 * unreachable, not that the list is wrong).
 */
export const FALLBACK_VAT_CATEGORIES: VatCategoryDTO[] = [
  { code: "food_zero",     name: "Food (zero rate)",     rate_basis_points:    0, active: true },
  { code: "food_standard", name: "Food (standard rate)", rate_basis_points: 2000, active: true },
  { code: "alcohol",       name: "Alcohol",              rate_basis_points: 2000, active: true },
  { code: "packaging",     name: "Packaging",            rate_basis_points: 2000, active: true },
];

interface ListResponse {
  vat_categories: VatCategoryDTO[];
}

export interface UseVatCategoriesResult {
  data: VatCategoryDTO[]; // ALWAYS populated (fetched OR fallback)
  isLoading: boolean;
  isError: boolean;
}

export function useVatCategories(): UseVatCategoriesResult {
  const [state, setState] = useState<UseVatCategoriesResult>({
    data: FALLBACK_VAT_CATEGORIES,
    isLoading: true,
    isError: false,
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get<ListResponse>("/v1/admin/catalog/vat-categories")
      .then((res) => {
        if (cancelled) return;
        setState({ data: res.vat_categories, isLoading: false, isError: false });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn(
          { context: "useVatCategories", err },
          "vat_categories_fetch_failed",
        );
        setState({
          data: FALLBACK_VAT_CATEGORIES,
          isLoading: false,
          isError: true,
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
